/** @jsxImportSource theme-ui */
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Flex } from 'theme-ui';
import { buildEmbedScriptForLevel } from '@/containers/widget/embedScript';
import {
    parseArticleCommand,
    parsePriceCommand,
    pickDefaultConfigurationId,
} from './embedPageState';
import { toCustomerData, validateCustomerForm } from './customerDataValidation';
import { useEmbedPageState } from './useEmbedPageState';
import { useEmbedPreviewController } from './useEmbedPreviewController';
import { CompanyEmbedData } from './useCompanyEmbedData';
import { useCompanyServicePrices } from './useCompanyServicePrices';
import EmbedConfigurationColumn from './EmbedConfigurationColumn';
import EmbedCodeColumn from './EmbedCodeColumn';
import EmbedPreviewColumn from './EmbedPreviewColumn';
import PreviewConsole from './PreviewConsole';

const STACK_BELOW = '@media only screen and (max-width:60em)';

const EmbedColumns: React.FC<{ data: CompanyEmbedData }> = ({ data }) => {
    const { companyId } = useParams<{ companyId: string }>();
    const { state, updateState, updateCustomer } = useEmbedPageState();
    const selectedConfigurationId =
        state.configurationId ?? pickDefaultConfigurationId(data.configurations);
    const customerErrors = useMemo(() => validateCustomerForm(state.customer), [state.customer]);
    const customerData = useMemo(() => toCustomerData(state.customer), [state.customer]);
    const articleCommand = useMemo(
        () => parseArticleCommand(state.articleId, state.quantity),
        [state.articleId, state.quantity]
    );
    const priceCommand = useMemo(
        () => parsePriceCommand(state.priceId, state.priceQuantity),
        [state.priceId, state.priceQuantity]
    );
    const prices = useCompanyServicePrices(companyId, state.level === 'prices');
    const snippet = buildEmbedScriptForLevel(state.level, {
        embedScriptUrl: data.embedScriptUrl,
        companySitepath: data.companySitepath,
        configId: selectedConfigurationId ?? '',
        fitViewport: state.fitViewport,
        example: state.articlesExample,
        articleId: articleCommand?.articleId ?? null,
        syncedArticleIds: state.syncedArticleIds,
        customer: customerData ?? {},
        pricesExample: state.pricesExample,
        priceId: priceCommand?.priceId ?? null,
        syncedPriceIds: state.syncedPriceIds,
    });
    const controller = useEmbedPreviewController({
        configurationId: selectedConfigurationId ?? '',
        companySitepath: data.companySitepath,
        embedScriptUrl: data.embedScriptUrl,
        fitViewport: state.fitViewport,
        level: state.level,
        articlesExample: state.articlesExample,
        syncedArticleIds: state.syncedArticleIds,
        pricesExample: state.pricesExample,
        syncedPriceIds: state.syncedPriceIds,
    });

    return (
        <Flex sx={{ gap: 3, alignItems: 'stretch', [STACK_BELOW]: { flexDirection: 'column' } }}>
            <Flex sx={{ flex: 1, minWidth: 0, flexDirection: 'column' }}>
                <Flex
                    sx={{
                        gap: 3,
                        alignItems: 'flex-start',
                        [STACK_BELOW]: { flexDirection: 'column' },
                    }}
                >
                    <Box sx={{ flex: '0 0 340px', minWidth: 0, maxWidth: '100%' }}>
                        <EmbedConfigurationColumn
                            state={state}
                            onChange={updateState}
                            onCustomerChange={updateCustomer}
                            configurations={data.configurations}
                            selectedConfigurationId={selectedConfigurationId}
                            articles={data.articles}
                            articlesTotal={data.articlesTotal}
                            prices={prices}
                            customerErrors={customerErrors}
                            preview={{
                                isWidgetReady: controller.session.isReady,
                                articleCommand,
                                sendArticleQuantity: controller.sendArticleQuantity,
                                priceCommand,
                                sendPriceQuantity: controller.sendPriceQuantity,
                                customerData,
                                sendCustomerData: controller.sendCustomerData,
                            }}
                        />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <EmbedCodeColumn
                            level={state.level}
                            onLevelChange={(level) => updateState({ level })}
                            articlesExample={state.articlesExample}
                            onArticlesExampleChange={(articlesExample) =>
                                updateState({ articlesExample })
                            }
                            pricesExample={state.pricesExample}
                            onPricesExampleChange={(pricesExample) =>
                                updateState({ pricesExample })
                            }
                            snippet={snippet}
                            isCopyDisabled={!data.embedScriptUrl || !selectedConfigurationId}
                        />
                    </Box>
                </Flex>
                <PreviewConsole entries={controller.log} onClear={controller.clearLog} />
            </Flex>
            <Box sx={{ flex: '0 0 420px', minWidth: 0, maxWidth: '100%' }}>
                {selectedConfigurationId && data.embedScriptUrl && (
                    <EmbedPreviewColumn
                        controller={controller}
                        fitViewport={state.fitViewport}
                        level={state.level}
                        articlesExample={state.articlesExample}
                        pricesExample={state.pricesExample}
                        customerData={customerData}
                    />
                )}
            </Box>
        </Flex>
    );
};

export default EmbedColumns;
